import { useMemo } from 'react'
import useItems from '~/prix/react/hooks/items'
import agentReportVisitLocationsListQuery from '../queries/agentReportVisitLocationsList.query'
import { QueryBase } from '~/prix/query'
import { AgentReportValues } from '../types/agentReport.types'

export interface VisitLocation {
  attendanceId: number | string
  legalEntityId: string
  legalEntityName: string
  legalEntityState?: string
  legalEntityCoordinates?: { coordinates?: [number, number] }
  legalEntityDataSource?: number
  attendanceStartDate?: string
  serviceUnitCoordinates?: any
  cityId?: number
  legalEntitySize?: string
  isBranch?: boolean
}

export interface UseAttendantVisitLocationsParams {
  agent: { id: string; name: string }
  startDate: string
  endDate: string
  period?: { value?: string }
  autoLoad?: boolean
}

export interface UseAttendantVisitLocationsReturn {
  visitLocations: VisitLocation[]
  isLoading: boolean
  isLoadingComparison?: boolean
  error?: any
  validAttendances: VisitLocation[]
  validAttendancesComparison?: VisitLocation[]
  attendancesCount: number
}

/**
 * Hook para centralizar a query attendantVisitLocationsListQuery
 * Evita múltiplas chamadas desnecessárias da mesma query em diferentes componentes
 */
export function useAttendantVisitLocations({
  agent,
  startDate,
  endDate,
  autoLoad = false,
}: UseAttendantVisitLocationsParams): UseAttendantVisitLocationsReturn {
  const values: AgentReportValues = useMemo(
    () => ({
      attendant: { id: agent.id, name: agent.name },
      period: {},
    }),
    [agent.id, agent.name],
  )

  const visitLocationsQuery = useItems(
    () => {
      return agent
        ? (agentReportVisitLocationsListQuery({
            ...values,
            startDate,
            endDate,
          }) as QueryBase)
        : (null as never)
    },
    [values, startDate, endDate],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: !!agent.id && autoLoad,
    },
  )

  const validAttendances = useMemo((): VisitLocation[] => {
    if (!visitLocationsQuery.items) return []

    const uniqueMap = new Map<string, VisitLocation>()

    for (const item of visitLocationsQuery.items as unknown as VisitLocation[]) {
      const hasCoordinates = item.legalEntityCoordinates?.coordinates !== undefined
      const validDataSource = item.legalEntityDataSource === 6

      if (!hasCoordinates || !validDataSource) {
        continue
      }

      const attendanceId = String(item.attendanceId)
      if (attendanceId && !uniqueMap.has(attendanceId)) {
        uniqueMap.set(attendanceId, item)
      }
    }

    return Array.from(uniqueMap.values())
  }, [visitLocationsQuery.items])

  const attendancesCount = validAttendances.length

  return {
    visitLocations: (visitLocationsQuery.items as unknown as VisitLocation[]) || [],
    isLoading: visitLocationsQuery.isLoading,
    error: visitLocationsQuery.error,
    validAttendances,
    attendancesCount,
  }
}
